import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/artificial-turf.js';



export default function ArtificialTurfPage() {
  const service = {
    title: 'Artificial Turf',
    image: '/hero/best-artificial-turf-landscaping-services-in-los-angeles.webp',
    description:
      'Install artificial turf with Modern Build. Enjoy a lush, low-maintenance lawn all year. Free estimate: (323) 300-4130',
    link: '/services/artificial-turf',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Artificial Turf Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Artificial Turf Installation | Free Estimate | Modern Build</title>
        <meta name="title" content="Artificial Turf Installation | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Install artificial turf with Modern Build. Enjoy a lush, low-maintenance lawn all year. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="artificial turf Los Angeles, synthetic grass, turf installation, fake grass, low-maintenance lawn, landscaping LA"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/artificial-turf" />

        <meta property="og:title" content="Artificial Turf Installation | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Install artificial turf with Modern Build. Enjoy a lush, low-maintenance lawn all year. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-artificial-turf-landscaping-services-in-los-angeles.webp"
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/services/artificial-turf"
        />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Artificial Turf Installation | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Install artificial turf with Modern Build. Enjoy a lush, low-maintenance lawn all year. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-artificial-turf-landscaping-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Artificial Turf Installation | Free Estimate | Modern Build',
                description:
                  'Upgrade your yard with professional artificial turf installation by Modern Build in Los Angeles. Enjoy a year-round green lawn with zero hassle.',
                slug: '/services/artificial-turf',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="artificialTurf" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="LANDSCAPING SERVICES"
        title="Install Artificial Turf for a Greener Future"
        background="white"
        description="Modern Build offers high-quality artificial turf solutions that give you a perfect lawn with no watering, mowing, or mud. Designed for Southern California, our turf products mimic real grass and stay vibrant all year."
        extraText="We handle everything from design to grading and installation. Whether you're transforming your backyard, front yard, or side yard, we ensure a flawless finish that enhances your curb appeal and saves water."
        phone="(323) 300-4130"
        image1="/split/modern-backyard-artificial-turf-installation.webp"
        image2="/split/clean-lawn-fake-grass-los-angeles.webp"
      />

      <Benefits
        title="Why Choose Artificial Turf from Modern Build?"
        subtitle="Smart, sustainable landscaping for LA homeowners"
        items={[
          { icon: <ShieldCheck className="w-8 h-8 text-primary" />, title: "Safe for Kids & Pets" },
          { icon: <CheckCircle className="w-8 h-8 text-primary" />, title: "Real-Grass Appearance" },
          { icon: <Wrench className="w-8 h-8 text-primary" />, title: "Professional Grading & Drainage" },
          { icon: <Ruler className="w-8 h-8 text-primary" />, title: "Precision Cuts & Seams" },
          { icon: <Hammer className="w-8 h-8 text-primary" />, title: "Durable Multi-Layer Base" },
          { icon: <DollarSign className="w-8 h-8 text-primary" />, title: "Water Bill Savings", href: "/services/solar-panels",},
          { icon: <ThumbsUp className="w-8 h-8 text-primary" />, title: "No Mowing or Fertilizing" },
          { icon: <Home className="w-8 h-8 text-primary" />, title: "Curb Appeal Boost" },
        ]}
      />

      <ContentBlock
        subtitle="LOS ANGELES TURF INSTALLATION EXPERTS"
        title="Green Grass Without the Hassle"
        description="Our artificial turf is engineered for Southern California’s climate and lifestyle. It looks real, drains efficiently, and stands up to foot traffic, pets, and sun."
        extraText="Perfect for front yards, backyards, playgrounds, and even putting greens. Enjoy a clean, consistent landscape that’s always photo-ready."
        buttonText="Get a Free Turf Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Turf Installation Process"
        subtitle="End-to-end service, built to last"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Site Evaluation & Design",
            description: "We assess your space, drainage needs, and sun exposure to plan the perfect layout.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Ground Preparation",
            description: "We remove existing grass, install a proper base, and grade the surface for stability and drainage.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Turf Installation",
            description: "Our team cuts, positions, and secures the turf using professional tools and adhesives.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Groom & Inspection",
            description: "We infill, brush, and inspect the lawn to ensure a clean, natural look that lasts for years.",
          },
        ]}
      />

      <Split
        subtitle="WATER-SAVING & STYLISH"
        title="The Perfect Lawn, Year-Round"
        background="white"
        description="Forget the sprinkler schedules. Our artificial turf gives you lush, green grass in any season while cutting water use and eliminating maintenance headaches."
        extraText="Let Modern Build transform your outdoor space into a low-maintenance showpiece."
        phone="(323) 300-4130"
        image1="/split/best-synthetic-grass-installation-los-angeles.webp"
        image2="/split/modern-lawn-with-fake-grass.webp"
        reverse={true}
      />

      <CTASection
        title="Book a Free Artificial Turf Estimate"
        subtitle="Upgrade your lawn with premium synthetic grass"
        buttonText="Get My Free Quote"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="artificial-turf" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
